import { Button, FormControl, Stack, TextField, InputAdornment, Alert, Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { User } from '@/_models/User';
import { DescriptionField } from '../components/NoteDescriptionField';
import { BottomBar } from '../../ui/BottomBar';
import { Select } from '@/components/common/SelectField';
import { AddNoteMode, NoteFormat, NoteIntegration, NotePriority, NoteType } from '../../../enums';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { FieldsGroup } from '../../ui/FieldsGroup';
import { useNotesContext } from '../../../hooks/useNotesContext';
import { useEditPosition } from '../../../hooks/useEditPosition';
import { HandleAddArgs } from '../../note';
import { NoteTypeData, teams } from '../../../data/task-manager';
import { PriorityField } from './components/priority-field';
import { getInitials } from '@/utils/getInitials';
import { Assignee } from '../../../types';
import { DueDateSelect } from './components/due-date';
import { ToggleField } from './components/toggle-field';
import { ICLFaultType } from '../../../enums/icl';
import { DefaultDatePicker } from '@/components/common/DatePicker';
import { useDueDate } from './hooks/useDueDate';
import { getDayMinutes } from './utils/date';
import { Avatar } from '../../ui/Avatar';
import { availableFromTimeSlots } from '../../../data';
import { ICLFaultList, ICLFaultTypeText } from '../../../data/icl';

export const priorityDueDateMap: Record<NotePriority, number> = {
  [NotePriority.Highest]: 0, // immediate
  [NotePriority.High]: 1, // 1 day
  [NotePriority.Medium]: 7, // 1 week
  [NotePriority.Low]: 30, // 1 month
  [NotePriority.Lower]: 30 * 3, // 3 months
  [NotePriority.Lowest]: 365, // 1 year
};

const maxFilesSize = 20971520;

const defaultAvailableFrom = () => {
  const todayTotalMinutes = getDayMinutes(new Date());
  const filteredTimeSlots = availableFromTimeSlots.filter((minutes) => {
    return minutes >= todayTotalMinutes;
  });

  // if there is no free slots today then set available from to the first slot of the next day
  if (filteredTimeSlots.length === 0) {
    return dayjs().startOf('day').add(1, 'day').add(availableFromTimeSlots[0], 'minute').toDate();
  }

  return dayjs().startOf('day').add(filteredTimeSlots[0], 'minute').toDate();
};

type Props = {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  handleAdd: (arg: HandleAddArgs) => Promise<string>;
  isNewThread: boolean;
  foundUser?: User;
  isMainMessage?: boolean;
  onCloseDrawer: () => void;
  messageId?: string;
  initialValues?: Partial<{
    title: string;
    description: string;
    assigneeId: string;
    priority: NotePriority;
    dueDate: Date;
    backlogEnabled: boolean;
    noteType: NoteType;
    isEquipmentFailure: boolean;
    availableFrom: Date;
    faultType: ICLFaultType;
    faultListIndex: number;
    tagId: number;
  }>;
};

export const NoteForm = ({
  files,
  setFiles,
  handleAdd,
  isNewThread,
  foundUser,
  isMainMessage,
  messageId,
  initialValues,
  onCloseDrawer,
}: Props) => {
  const {
    addingWorkflow,
    userId,
    mediaUrls,
    setMediaUrls,
    setNoteToEdit,
    noteToEdit,
    tags,
    integration,
  } = useNotesContext();
  const { handleCancleEditPosition, handleEditPosition, handleSavePosition } = useEditPosition();
  const { tour, collaborators } = useNotesContext();

  // For now show only teams
  const assignees: Assignee[] = useMemo(() => {
    // const users: Assignee[] = collaborators
    // .filter((c) => c.id !== userId)
    // .map((c) => ({ name: c.name, id: c.id.toString() }));

    return [
      ...teams,
      // ...users
    ];
  }, [collaborators, userId]);

  const [assigneeId, setAssigneeId] = useState<string | null>(
    initialValues?.assigneeId || assignees?.[0]?.id.toString() || '',
  );
  const [description, setDescription] = useState(initialValues?.description || '');
  const [title, setTitle] = useState(initialValues?.title || '');
  const [focused, setFocused] = useState(false);
  const [previewSrcs, setPreviewSrcs] = useState<
    { name?: string; lastModified?: number; src?: string }[]
  >([]);
  const [audioURLs, setAudioURLs] = useState<{ lastModified?: number; src?: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [descriptionLength, setDescriptionLength] = useState<number>(0);
  const [loadingFiles, setLoadingFiles] = useState([]);
  const [noteType, setNoteType] = useState<NoteType>(initialValues?.noteType || NoteType.General);
  const [titleError, setTitleError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');
  const [priority, setPriority] = useState<NotePriority>(
    initialValues?.priority || NotePriority.Medium,
  );
  const [backlogEnabled, setBacklogEnabled] = useState(initialValues?.backlogEnabled ?? true);

  // ICL
  const [isEquipmentFailure, setIsEquipmentFailure] = useState<boolean>(
    initialValues?.isEquipmentFailure ?? false,
  );
  const [availableFrom, setAvailableFrom] = useState<Date>(
    initialValues?.availableFrom || defaultAvailableFrom(),
  );
  const [faultType, setFaultType] = useState<ICLFaultType>(
    initialValues?.faultType || ICLFaultType.Mechanical,
  );
  const [faultListIndex, setFaultListIndex] = useState<number>(initialValues?.faultListIndex ?? 0);
  const [selectedTagId, setSelectedTagId] = useState(
    initialValues?.tagId ?? addingWorkflow?.tag?.id,
  );
  const selectedTagData = tags.find((t) => t.id === selectedTagId);

  useEffect(() => {
    if (addingWorkflow?.active && addingWorkflow.mode === AddNoteMode.Tag) {
      setSelectedTagId(addingWorkflow.tag?.id);
    }
  }, [addingWorkflow]);

  const { dueDateError, dueDate, setDueDate } = useDueDate({
    availableFrom,
    assigneeId,
    backlogEnabled,
    defaultPriority: priority,
    initialDueDate: initialValues?.dueDate,
    noteId: noteToEdit?.note?.id,
  });

  const inputRef = useRef(null);
  const taggingEl = useRef(null);

  const clearInput = () => {
    setDescription('');
    setTitle('');
    setFocused(false);
    setFiles([]);
    setPreviewSrcs([]);
    setAudioURLs([]);
    setIsLoading(false);
    // setHasShownError(false);
    setAssigneeId(assignees?.[0]?.id.toString() || '');
    setAddButtonDisabled(true);
    setNoteToEdit(null);
    setNoteType(NoteType.General);
    setPriority(NotePriority.Medium);
    setBacklogEnabled(true);
    setIsEquipmentFailure(false);
    setAvailableFrom(defaultAvailableFrom());
    setFaultType(ICLFaultType.Mechanical);
    setFaultListIndex(0);
  };

  const handleChangeTitle = (e) => {
    const { value } = e.target;
    if (value) {
      setTitleError('');
    }
    if (userId) {
      setTitle(value);
    }
  };

  const handleClickOutside = (event) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target) &&
      taggingEl.current &&
      !taggingEl.current.contains(event.target)
    ) {
      setFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePriorityChange = (newPriority: NotePriority) => {
    setPriority(newPriority);

    const daysToAdd = priorityDueDateMap[newPriority];
    const newDueDate = dayjs(integration === NoteIntegration.ICL ? availableFrom : new Date())
      .add(daysToAdd, 'day')
      .startOf('day')
      .add(getDayMinutes(dueDate), 'minute')
      .toDate();

    setDueDate(newDueDate);
  };

  useEffect(() => {
    let hasErrors = false;

    if (title === '' && isNewThread) {
      setTitleError('This field cannot be empty');
      hasErrors = true;
    } else {
      setTitleError('');
    }

    if (description === '' && !isNewThread) {
      setDescriptionError('This field cannot be empty');
      hasErrors = true;
    } else {
      setDescriptionError('');
    }

    // if (dueDateError) {
    //   hasErrors = true;
    // }

    setAddButtonDisabled(hasErrors);
  }, [
    title,
    descriptionLength,
    mediaUrls,
    previewSrcs,
    audioURLs,
    description,
    dueDate,
    isNewThread,
    dueDateError,
  ]);

  useEffect(() => {
    if (!!noteToEdit?.note && !noteToEdit?.note?.tagId && isMainMessage) {
      handleEditPosition(noteToEdit.note);
    }
  }, [noteToEdit]);

  const handleFileChange = (event) => {
    const uploadedFiles: File[] = Array.from(event.target.files);

    if (uploadedFiles.length) {
      const fileReaders = uploadedFiles.map((file) => {
        return new Promise((resolve, reject) => {
          const { type } = file;

          setLoadingFiles((prevLoading) => [
            ...prevLoading,
            { name: file.name, lastModified: file.lastModified },
          ]);

          if (type === 'image/png' || type === 'image/jpeg' || type === 'image/webp') {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = (e) => {
              resolve({
                name: file.name,
                lastModified: file.lastModified,
                src: e.target.result,
                type: 'image',
              });
            };

            reader.onerror = (e) => {
              reject(e);
            };
          } else if (type.startsWith('video/')) {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'video',
            });
          } else if (type === 'application/pdf') {
            resolve({
              name: file.name,
              lastModified: file.lastModified,
              src: URL.createObjectURL(file),
              type: 'pdf',
            });
          } else {
            toast.error('Unsupported file format');
            reject(new Error('Unsupported file format'));
          }
        });
      });

      Promise.allSettled(fileReaders).then((results) => {
        const successfulResults = results
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<{
              name: string;
              lastModified: number;
              src: string;
            }> => result.status === 'fulfilled',
          )
          .map((result) => result.value);

        const successfulFiles = uploadedFiles.filter(
          (_, index) => results[index].status === 'fulfilled',
        );

        setLoadingFiles([]);

        if (successfulResults.length) {
          setPreviewSrcs((prevSrcs) => [...prevSrcs, ...successfulResults]);
          setFiles((prevUploads) => [...prevUploads, ...successfulFiles]);
        }
      });

      event.target.value = null;
    }
  };

  const removeReview = ({ name, lastModified }: { name?: string; lastModified?: number }) => {
    const filteredPreviews = previewSrcs.filter(
      (preview) => preview.name !== name && preview.lastModified !== lastModified,
    );
    const filteredFiles = files.filter(
      (file) => file.name !== name && file.lastModified !== lastModified,
    );

    setPreviewSrcs(filteredPreviews);
    setFiles(filteredFiles);
  };

  const removeAudio = ({ lastModified }: { lastModified: number }) => {
    const filteredAudios = audioURLs.filter((audioUrl) => audioUrl.lastModified !== lastModified);
    const filteredFiles = files.filter((file) => file.lastModified !== lastModified);

    setAudioURLs(filteredAudios);
    setFiles(filteredFiles);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearInput();
    if (!noteToEdit?.note?.tagId && noteToEdit?.note && isMainMessage) {
      handleCancleEditPosition(noteToEdit.note);
    }

    if (isNewThread) {
      onCloseDrawer();
    }
  };

  const handleSave = async () => {
    setAddButtonDisabled(true);
    setIsLoading(true);
    let newPosition;
    if (isMainMessage && !noteToEdit?.note?.tagId && noteToEdit?.note) {
      newPosition = await handleSavePosition({ ...noteToEdit?.note });
    }
    const result = await handleAdd({
      title,
      description,
      messageId,
      isMainMessage,
      format: NoteFormat.General,
      SAPData: {},
      assigneeId,
      tagId: selectedTagId,
      taskManager: {
        noteType,
        priority,
        dueDate,
        backlog: backlogEnabled,
      },
      icl: {
        faultType,
        faultListIndex,
        isEquipmentFailure,
        availableFrom,
      },
      position: newPosition,
    });

    if (result === 'Success') {
      setFocused(false);
      clearInput();
    } else {
      toast.error('Could not send message');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const uploadedFilesSize: number = files.reduce((acc, file) => acc + file.size, 0);

    if (files.length + mediaUrls?.length > 10) {
      setAddButtonDisabled(true);
      toast.error('Too many files, max count is 10');
    } else if (uploadedFilesSize > maxFilesSize) {
      setAddButtonDisabled(true);
      toast.error('Files are too large, max is 20mb');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const shouldShowTaskManagerFields =
    tour.notesTaskManagerModeEnabled && noteType !== NoteType.General;

  return (
    <>
      <FormControl sx={{ height: '100%', p: noteToEdit ? '0px' : '18px' }}>
        <Stack direction="column" gap="10px" height="100%">
          {tour?.notesTaskManagerModeEnabled && (
            <Field>
              <FieldLabel>Note type</FieldLabel>
              <Select
                value={noteType}
                menuWidth="250px"
                onChange={(v) => {
                  setNoteType(v.value);
                }}
                options={[
                  { label: NoteTypeData[NoteType.General].label, value: NoteType.General },
                  {
                    label: NoteTypeData[NoteType.PreventiveMaintenance].label,
                    value: NoteType.PreventiveMaintenance,
                    icon: (
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: NoteTypeData[NoteType.PreventiveMaintenance].color,
                        }}
                      />
                    ),
                  },
                  {
                    label: NoteTypeData[NoteType.Fault].label,
                    value: NoteType.Fault,
                    icon: (
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          background: NoteTypeData[NoteType.Fault].color,
                        }}
                      />
                    ),
                  },
                ]}
              />
            </Field>
          )}

          <FieldsGroup
            label="Properties"
            tooltip="Properties"
            // passThrough={!!noteToEdit || !isNewThread}
            defaultExpanded
          >
            {integration === NoteIntegration.ICL && noteType !== NoteType.General ? (
              <>
                <Field>
                  <FieldLabel>Fault Type</FieldLabel>
                  <Select
                    value={faultType}
                    onChange={(option) => {
                      setFaultType(option.value);
                      setFaultListIndex(0);
                    }}
                    options={Object.values(ICLFaultType).map((type) => ({
                      label: ICLFaultTypeText[type],
                      value: type,
                    }))}
                  />
                </Field>

                <Field>
                  <FieldLabel>Fault List</FieldLabel>
                  <Select
                    value={faultListIndex}
                    onChange={(option) => setFaultListIndex(option.value)}
                    options={ICLFaultList[faultType].map((fault, index) => ({
                      label: fault,
                      value: index,
                    }))}
                  />
                </Field>
              </>
            ) : null}

            {isMainMessage && (
              <Field>
                <FieldLabel>Title</FieldLabel>
                <TextField
                  variant="outlined"
                  {...(noteToEdit ? { defaultValue: title } : { value: title })}
                  placeholder="Write a title"
                  onChange={handleChangeTitle}
                  inputProps={{
                    style: {
                      fontFamily: 'inherit',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      padding: '10px 14px',
                      height: '45px',
                    },
                  }}
                  InputProps={{
                    endAdornment: titleError ? (
                      <InputAdornment
                        position="end"
                        style={{ position: 'absolute', right: '14px', bottom: '50%' }}
                      >
                        <i
                          className="fa-regular fa-circle-exclamation"
                          style={{ color: 'red', fontSize: '16px' }}
                        />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: '100%',
                    borderRadius: '8px',
                    border: titleError ? 'none' : '1px solid #D0D5DD',
                    margin: 0,
                    background: titleError ? 'transparent' : '#FFF',
                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                    '& fieldset': { height: '100%', top: 0 },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#D0D5DD',
                      borderRadius: '8px',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      flexDirection: 'column',
                      '&.Mui-error': {
                        borderColor: 'red',
                        background: '#FFF',
                      },
                    },
                    fontWeight: 400,
                  }}
                  InputLabelProps={{
                    style: {
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '24px',
                      color: '#667085',
                      opacity: 1,
                    },
                  }}
                  error={!!titleError}
                  helperText={titleError}
                />
              </Field>
            )}

            <Field>
              {(isNewThread || !!noteToEdit) && (
                <FieldLabel tooltip="tooltip">Description</FieldLabel>
              )}
              <DescriptionField
                focused={focused}
                setFocused={setFocused}
                foundUser={foundUser}
                isNewThread={isNewThread}
                editMode={!!noteToEdit}
                setFiles={setFiles}
                addButtonDisabled={addButtonDisabled}
                setAddButtonDisabled={setAddButtonDisabled}
                setIsLoading={setIsLoading}
                handleAdd={handleAdd}
                clearInput={clearInput}
                description={description}
                setDescription={setDescription}
                setAudioURLs={setAudioURLs}
                previewSrcs={previewSrcs}
                loadingFiles={loadingFiles}
                audioURLs={audioURLs}
                mediaUrls={mediaUrls}
                isLoading={isLoading}
                removeReview={removeReview}
                removeAudio={removeAudio}
                setMediaUrls={setMediaUrls}
                handleFileChange={handleFileChange}
                descriptionLength={descriptionLength}
                setDescriptionLength={setDescriptionLength}
                descriptionError={descriptionError}
              />
            </Field>
          </FieldsGroup>

          {(addingWorkflow?.mode === AddNoteMode.Tag || !!noteToEdit?.note?.tagId) && (
            <FieldsGroup label="Details" tooltip="Details" defaultExpanded>
              <Field>
                <FieldLabel>
                  {integration === NoteIntegration.ICL ? 'Functional Location' : 'Tag'}
                </FieldLabel>
                <Select
                  value={selectedTagId}
                  options={tags.map((t) => ({ value: t.id, label: t.title }))}
                  disabled={addingWorkflow?.isTagStatic}
                  onChange={({ value }) => {
                    setSelectedTagId(value);
                    // const tag = tags.find((t) => t.id.toString() === value);
                    // setAddingWorkflow((prev) => ({ ...prev, tag }));
                  }}
                />
              </Field>

              {selectedTagData?.description ? (
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: '#f2f4f7',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    alignItems: 'center',
                    padding: '14px !important',
                    '& .MuiAlert-message': {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'var(--Gray-600, #475467)',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '24px',
                      padding: '0 !important',
                      flexGrow: 1,
                    },
                    '& .MuiAlert-icon': {
                      color: '#667085',
                      padding: '0',
                      marginRight: '8px',
                    },
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: selectedTagData.description }} />
                </Alert>
              ) : null}

              {integration === NoteIntegration.ICL && noteType !== NoteType.General && (
                <ToggleField
                  enabled={isEquipmentFailure}
                  onChange={setIsEquipmentFailure}
                  label="Equipment Failure"
                  tooltip="Equipment Failure"
                />
              )}
            </FieldsGroup>
          )}

          {shouldShowTaskManagerFields ? (
            <FieldsGroup label="Task Info" tooltip="Task info" defaultExpanded>
              {integration === NoteIntegration.ICL && (
                <Field>
                  <FieldLabel>Available From</FieldLabel>
                  <DefaultDatePicker
                    value={availableFrom}
                    onChange={(date) => {
                      setAvailableFrom(date);
                    }}
                    timeSlots={Array.from({ length: 19 }, (_, i) => i * 30 + 480)}
                    disableDaysBefore={new Date()}
                    shouldDisableDay={({ date, isToday }) => {
                      const todayTotalMinutes = getDayMinutes(date.toDate());
                      const filteredTimeSlots = availableFromTimeSlots.filter((minutes) => {
                        return minutes >= todayTotalMinutes;
                      });

                      if (isToday && filteredTimeSlots.length === 0) {
                        return true;
                      }

                      return false;
                    }}
                    shouldDisableTime={(minutes) => {
                      return (
                        Date.now() <
                        dayjs()
                          .hour(0)
                          .minute(0)
                          .second(0)
                          .millisecond(0)
                          .add(minutes, 'minute')
                          .unix()
                      );
                    }}
                  />
                </Field>
              )}

              <PriorityField value={priority} onChange={handlePriorityChange} />

              <Field>
                <FieldLabel>Due Date</FieldLabel>
                <DueDateSelect
                  dueDate={dueDate}
                  onChange={setDueDate}
                  backlogEnabled={backlogEnabled}
                  availableFrom={integration === NoteIntegration.ICL ? availableFrom : new Date()}
                  assigneeId={assigneeId}
                  noteId={noteToEdit?.note?.id}
                />
                <FieldError>{dueDateError}</FieldError>
              </Field>

              <Field>
                <FieldLabel>Assignee</FieldLabel>
                <Select
                  value={assigneeId}
                  options={assignees.map((c) => ({
                    value: c.id,
                    label: c.name,
                    icon: (
                      <Avatar alt={c.name} bgColor={c.color}>
                        {getInitials(c.name)}
                      </Avatar>
                    ),
                  }))}
                  onChange={({ value }) => {
                    setAssigneeId(value);
                  }}
                />
              </Field>

              <ToggleField
                enabled={backlogEnabled}
                onChange={setBacklogEnabled}
                label="Backlog"
                tooltip="Backlog toggle"
              />
            </FieldsGroup>
          ) : null}
        </Stack>
      </FormControl>

      {isNewThread && (
        <BottomBar justifyContent="flex-end" p="10px">
          <Button
            variant="outlined"
            size="small"
            sx={{
              border: '1px solid #D0D5DD',
              borderRadius: '8px',
              paddingX: '18px',
              paddingY: '10px',
              color: '#101828',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '24px',
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={addButtonDisabled}
            variant="contained"
            color="primary"
            size="small"
            sx={{
              background: '#2055FF',
              color: 'white',
              font: 'Inter',
              fontWeight: '500',
              fontSize: '16px',
              paddingX: '18px',
              paddingY: '10px',
              borderRadius: '8px',
              lineHeight: '24px',
            }}
            onClick={handleSave}
          >
            Create
          </Button>
        </BottomBar>
      )}

      {!!noteToEdit && (
        <Stack
          gap="10px"
          direction="row"
          mt="10px"
          sx={{
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            disabled={addButtonDisabled}
            variant="contained"
            color="primary"
            size="small"
            sx={{
              background: '#2055FF',
              color: 'white',
              font: 'Inter',
              fontWeight: '500',
              fontSize: '16px',
              paddingX: '18px',
              paddingY: '10px',
              borderRadius: '8px',
              lineHeight: '24px',
            }}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            size="small"
            sx={{
              border: '1px solid #D0D5DD',
              borderRadius: '8px',
              paddingX: '18px',
              paddingY: '10px',
              color: '#101828',
              fontWeight: '500',
              fontSize: '16px',
              lineHeight: '24px',
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Stack>
      )}
    </>
  );
};
